import { useGetTaxonomyChildren } from '@/hooks/api/taxonomy/useGetTaxonomyChildren';
import type { TaxonomyNode } from '@/api/generatedApi';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { Add, Check, Close, Delete, Edit, FolderOpen, Label } from '@mui/icons-material';
import { Box, Chip, CircularProgress, IconButton, TextField } from '@mui/material';
import { memo, useEffect, useRef, useState } from 'react';
import { TaxonomyNodeLevel } from './TaxonomyNodeLevel';
import { AddNodeInput } from './AddNodeInput';
import { LoadingIndicator } from './LoadingIndicator';
import { getDepthColor, type TaxonomyTreeProperties } from './taxonomyTreeConstants';

const RenameInput = ({
  initialName,
  onSubmit,
  onCancel,
  isRenaming,
}: {
  initialName: string;
  onSubmit: (name: string) => void;
  onCancel: () => void;
  isRenaming: boolean;
}) => {
  const [name, setName] = useState(initialName);
  const containerReference = useRef<HTMLDivElement>(null);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === 'Enter' && name.trim() && !isRenaming) {
      if (name.trim() === initialName) {
        onCancel();
      } else {
        onSubmit(name.trim());
      }
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    }
  };

  const canSubmit = name.trim().length > 0 && name.trim() !== initialName && !isRenaming;

  const handleSubmit = () => {
    if (canSubmit) {
      onSubmit(name.trim());
    }
  };

  return (
    <Box
      ref={containerReference}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
      onClick={event => event.stopPropagation()}
    >
      <TextField
        size='small'
        value={name}
        onChange={event => setName(event.target.value)}
        onKeyDown={handleKeyDown}
        onKeyUp={event => {
          event.stopPropagation();
          if (event.key === 'Escape') onCancel();
        }}
        onBlur={event => {
          if (!containerReference.current?.contains(event.relatedTarget as Node)) {
            onCancel();
          }
        }}
        autoFocus
        disabled={isRenaming}
        slotProps={{
          input: {
            endAdornment: isRenaming ? <CircularProgress size={16} /> : undefined,
            sx: { fontSize: '0.875rem', height: 28 },
          },
        }}
        sx={{ width: 200 }}
      />
      <IconButton size='small' onClick={handleSubmit} disabled={!canSubmit} title='Save' sx={{ p: 0.25 }}>
        <Check sx={{ fontSize: 18, color: canSubmit ? 'success.main' : undefined }} />
      </IconButton>
      <IconButton size='small' onClick={onCancel} disabled={isRenaming} title='Cancel' sx={{ p: 0.25 }}>
        <Close sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};

const hoverActionSx = {
  opacity: 0,
  transition: 'opacity 0.15s',
  '.MuiTreeItem-content:hover &': { opacity: 0.7 },
  '&:hover': { opacity: '1 !important' },
  p: 0.25,
};

const TaxonomyLazyTreeItemComponent = ({
  node,
  depth,
  ...treeProperties
}: { node: TaxonomyNode; depth: number } & TaxonomyTreeProperties) => {
  const {
    taxonomyId,
    isItemExpanded,
    expandAllActive,
    addExpandedItems,
    addingChildOfId,
    isCreating,
    onAddChild,
    onSubmitChild,
    onCancelAdd,
    renamingNodeId,
    isRenaming,
    onStartRename,
    onRenameNode,
    onCancelRename,
    onDeleteNode,
  } = treeProperties;

  const isExpanded = isItemExpanded(node.id);
  const isAddingHere = addingChildOfId === node.id;
  const isRenamingHere = renamingNodeId === node.id;

  const {
    nodes: prefetchedChildren,
    isLoading: childrenLoading,
    isSuccess,
  } = useGetTaxonomyChildren(taxonomyId, node.id, {
    enabled: (isExpanded || isAddingHere || expandAllActive) && (node.hasChildren || isAddingHere),
  });

  const hasChildrenFromFetch = isSuccess && prefetchedChildren.length > 0;
  const hasChildren = node.hasChildren || hasChildrenFromFetch;
  const color = getDepthColor(depth);
  const isSearchMatch = treeProperties.searchMatchIds?.has(node.id);
  const isDimmed = treeProperties.searchVisibleIds && !treeProperties.searchVisibleIds.has(node.id) && !isSearchMatch;

  useEffect(() => {
    if (expandAllActive && isSuccess && prefetchedChildren.length > 0) {
      addExpandedItems(prefetchedChildren.filter(n => n.hasChildren).map(n => n.id));
    }
  }, [expandAllActive, isSuccess, prefetchedChildren, addExpandedItems]);

  return (
    <TreeItem
      itemId={node.id}
      label={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            minHeight: 36,
          }}
        >
          {isRenamingHere ? (
            <RenameInput
              initialName={node.name}
              onSubmit={newName => onRenameNode(node.id, newName)}
              onCancel={onCancelRename}
              isRenaming={isRenaming}
            />
          ) : (
            <Chip
              icon={
                hasChildren ? (
                  <FolderOpen sx={{ fontSize: 16, color: `${color} !important` }} />
                ) : (
                  <Label sx={{ fontSize: 16, color: `${color} !important` }} />
                )
              }
              label={node.name}
              size='small'
              variant='outlined'
              sx={{
                borderColor: color,
                color: isDimmed ? 'text.secondary' : 'text.primary',
                fontWeight: hasChildren ? 500 : 400,
                '& .MuiChip-label': { px: 1 },
                height: 28,
                opacity: isDimmed ? 0.5 : 1,
              }}
            />
          )}
          {!isRenamingHere && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                size='small'
                onClick={event => {
                  event.stopPropagation();
                  onStartRename(node.id);
                }}
                title='Rename'
                sx={hoverActionSx}
              >
                <Edit sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size='small'
                onClick={event => {
                  event.stopPropagation();
                  onDeleteNode(node.id, node.name, hasChildren);
                }}
                title='Delete'
                sx={hoverActionSx}
              >
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size='small'
                onClick={event => {
                  event.stopPropagation();
                  onAddChild(node.id);
                }}
                title='Add subcategory'
                sx={hoverActionSx}
              >
                <Add sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          )}
        </Box>
      }
    >
      {childrenLoading && <LoadingIndicator count={1} />}
      {hasChildren && !isExpanded && <TreeItem itemId={`${node.id}-ph`} label='' sx={{ display: 'none' }} />}
      {isExpanded && hasChildren && (
        <TaxonomyNodeLevel
          parentNodeId={node.id}
          depth={depth + 1}
          {...treeProperties}
          {...(isSearchMatch && { searchVisibleIds: undefined })}
        />
      )}
      {isAddingHere && <AddNodeInput onSubmit={onSubmitChild} onCancel={onCancelAdd} isCreating={isCreating} />}
    </TreeItem>
  );
};

export const TaxonomyLazyTreeItem = memo(TaxonomyLazyTreeItemComponent);
